<template>
	<div class="container-fluid conn-page">
		<div class="connections-wrapper">

			<!-- Header -->
			<div class="page-header mt-4 mb-4">
        <back-btn to="/dashboard"></back-btn>
        <div>
					<h2 class="gold mb-0"><span v-text="this.$route.meta.title"></span></h2>
					<p v-if="!isBusy" class="conn-subtitle">{{ totalRows }} connection{{ totalRows !== 1 ? 's' : '' }}</p>
				</div>
			</div>

			<!-- Search bar -->
			<div v-if="connections.length > 5" class="conn-search-bar mb-3">
				<i class="fas fa-search conn-search-icon"></i>
				<input
					v-model="filter"
					type="search"
					class="conn-search-input"
					placeholder="Search connections..."
				/>
				<button v-if="filter" class="conn-search-clear" @click="filter = ''">
					<i class="fas fa-times"></i>
				</button>
			</div>

			<!-- Pending Connection Requests -->
			<div v-if="pendingRequests.length" class="community-section mb-4">
				<h4 class="section-heading mb-3">
					<i class="fas fa-user-clock mr-2"></i>
					Connection Requests
					<b-badge pill style="background:#d6b674;color:#fff;" class="ml-2">{{ pendingRequests.length }}</b-badge>
				</h4>

				<div
					v-for="member in pendingRequests"
					:key="'req-' + member.id"
					class="request-row"
				>
					<div class="member-row-avatar-wrap" v-b-tooltip.hover :title="member.is_available ? 'Available' : 'Unavailable'">
						<router-link :to="'/member/' + member.id + '?from=/connections'">
							<img
								:src="member.profile_image || defaultAvatar"
								:alt="member.full_name"
								class="member-row-avatar"
								@error="onImgError"
							/>
						</router-link>
						<span class="member-status-dot" :class="member.is_available ? 'dot-available' : 'dot-unavailable'"></span>
					</div>

					<div class="member-row-info">
						<router-link :to="'/member/' + member.id + '?from=/connections'" class="member-row-name-link" v-b-tooltip.hover title="View Profile">
							<p class="member-row-name">{{ member.full_name }}</p>
						</router-link>
						<p v-if="memberLocation(member)" class="member-row-location text-muted">
							<i class="fas fa-map-marker-alt mr-1"></i>
							{{ memberLocation(member) }}
						</p>
					</div>

					<div class="member-row-actions">
						<b-button size="sm" class="btn-gold mr-1" @click="acceptRequest(member)">
							<i class="fas fa-check mr-1"></i> Accept
						</b-button>
						<b-button size="sm" variant="outline-secondary" @click="declineRequest(member)">
							<i class="fas fa-times mr-1"></i> Decline
						</b-button>
						<b-button size="sm" class="btn-gold" @click="openRequestModal(member)">
							<i class="fas fa-hands-helping mr-1"></i> <span class="btn-text"> Request a process</span>
						</b-button>
					</div>
				</div>
			</div>

			<!-- Loading -->
			<div v-if="isBusy" class="conn-loading">
				<b-spinner style="color:#d6b674;"></b-spinner>
				<p class="mt-2 text-muted small">Loading connections...</p>
			</div>

			<!-- Empty -->
			<div v-else-if="!filteredMembers.length" class="conn-empty">
				<i class="fas fa-user-friends conn-empty__icon"></i>
				<p class="conn-empty__text">{{ filter ? 'No results found.' : 'No connections yet.' }}</p>
			</div>

			<!-- Member list -->
			<div v-else class="conn-list">
				<div
					v-for="member in filteredMembers"
					:key="member.id"
					class="conn-row"
				>
					<!-- Avatar + status -->
					<div class="conn-row__avatar-wrap">
						<router-link :to="'/member/' + member.id + '?from=/connections'">
							<img
								:src="member.profile_image || defaultAvatar"
								:alt="member.full_name"
								class="conn-row__avatar"
								@error="onImgError"
							/>
						</router-link>
						<span
							class="conn-row__dot"
							:class="member.is_available ? 'conn-row__dot--available' : 'conn-row__dot--unavailable'"
							v-b-tooltip.hover
							:title="member.is_available ? 'Available' : 'Unavailable'"
						></span>
					</div>

					<!-- Name + availability label -->
					<div class="conn-row__info">
						<router-link
							:to="'/member/' + member.id + '?from=/connections'"
							class="conn-row__name"
						>{{ member.full_name }}</router-link>
						<span class="conn-row__status" :class="member.is_available ? 'conn-row__status--available' : 'conn-row__status--unavailable'">
							{{ member.is_available ? 'Available' : 'Unavailable' }}
						</span>
					</div>

					<!-- Actions -->
					<div class="conn-row__actions">
						<button
							class="conn-action-btn conn-action-btn--process"
							v-b-tooltip.hover title="Request a process"
							@click="openRequestModal(member)"
						>
							<i class="fas fa-hands-helping"></i>
						</button>
						<router-link
							:to="`/chatbox/${member.id}?from=/connections`"
							class="conn-action-btn conn-action-btn--chat"
							v-b-tooltip.hover title="Send Message"
						>
							<i class="fas fa-comment-dots"></i>
						</router-link>
					</div>
				</div>
			</div>

			<process-request-popup
				v-if="showRequestModal"
				:requestTarget="requestTarget"
				v-model="showRequestModal"
			></process-request-popup>

		</div>
	</div>
</template>

<script>
import axios from '@/axios';
import ProcessRequestPopup from '@/components/Processing/ProcessRequestPopup.vue';
import BackBtn from "@/components/BackBtn.vue";

const DEFAULT_AVATAR = 'https://media.iamconnected.com/profile_images/default.png';

export default {
	name: 'Connections',
	components: {BackBtn, ProcessRequestPopup },
	data() {
		return {
			connections: [],
			isBusy: true,
			totalRows: 0,
			filter: '',
			showRequestModal: false,
			defaultAvatar: DEFAULT_AVATAR,
			requestTarget: null,
			pendingRequests: [],
		};
	},
	computed: {
		filteredMembers() {
			if (!this.connections.length) return [];
			if (!this.filter) return this.connections;
			const q = this.filter.toLowerCase();
			return this.connections.filter(m => m.full_name && m.full_name.toLowerCase().includes(q));
		},
	},
	methods: {
		async fetchCommunity() {
			this.isBusy = true;
			try {
				const response = await axios({ url: this.$store.state.api_url + 'communityGet' });
				if (response.data && response.data.status === 1) {
					this.connections    = response.data.connections || [];
					this.pendingRequests = response.data.pending_requests || [];
					this.totalRows      = this.connections.length;
				}
			} catch (err) {
				console.error('Error fetching connections:', err);
			} finally {
				this.isBusy = false;
			}
		},

		memberLocation(member) {
			const parts = [member.city, member.country].filter(Boolean);
			return parts.join(', ');
		},

		onImgError(e) {
			e.target.src = DEFAULT_AVATAR;
		},

		openRequestModal(member) {
			this.requestTarget = { id: member.id, full_name: member.full_name };
			this.showRequestModal = true;
		},

		async acceptRequest(member) {
			this.pendingRequests = this.pendingRequests.filter(m => m.id !== member.id);
			try {
				await axios({
					url: this.$store.state.api_url + 'connectionAccept',
					params: { sender_id: member.id },
				});
				this.fetchCommunity();
			} catch (err) {
				console.error('Accept error:', err);
				this.pendingRequests.push(member);
			}
		},

		async declineRequest(member) {
			this.pendingRequests = this.pendingRequests.filter(m => m.id !== member.id);
			try {
				await axios({
					url: this.$store.state.api_url + 'connectionDecline',
					params: { sender_id: member.id },
				});
			} catch (err) {
				console.error('Decline error:', err);
				this.pendingRequests.push(member);
			}
		},
	},
	mounted() {
		this.fetchCommunity();
	},
};
</script>

<style scoped>
/* ── Page ── */
.conn-page {
	padding: 0 15px 60px;
}

.connections-wrapper {
	max-width: 900px;
	margin: 0 auto;
}

/* ── Header ── */
.page-header {
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
	text-align: center;
}

.conn-subtitle {
	font-size: 0.78rem;
	color: #aaa;
	margin: 2px 0 0;
}

/* ── Search ── */
.conn-search-bar {
	position: relative;
	display: flex;
	align-items: center;
}

.conn-search-icon {
	position: absolute;
	left: 14px;
	color: #ccc;
	font-size: 0.85rem;
	pointer-events: none;
}

.conn-search-input {
	width: 100%;
	padding: 10px 36px 10px 38px;
	border: 1px solid #eee;
	border-radius: 12px;
	background: #fff;
	font-size: 0.88rem;
	color: #333;
	outline: none;
	transition: border-color 0.18s, box-shadow 0.18s;
}

.conn-search-input:focus {
	border-color: rgba(214, 182, 116, 0.6);
	box-shadow: 0 0 0 3px rgba(214, 182, 116, 0.1);
}

.conn-search-clear {
	position: absolute;
	right: 12px;
	background: none;
	border: none;
	color: #bbb;
	cursor: pointer;
	font-size: 0.8rem;
	padding: 0;
	line-height: 1;
}

.conn-search-clear:hover { color: #888; }

/* ── States ── */
.conn-loading,
.conn-empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 60px 20px;
	gap: 10px;
}

.conn-empty__icon {
	font-size: 2.4rem;
	color: #ddd;
	margin-bottom: 6px;
}

.conn-empty__text {
	color: #bbb;
	font-size: 0.9rem;
	margin: 0;
}

/* ── Member list ── */
.conn-list {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.conn-row {
	display: flex;
	align-items: center;
	gap: 14px;
	padding: 14px 16px;
	background: #fff;
	border: 1px solid #f0ece3;
	border-radius: 14px;
	box-shadow: 0 2px 8px rgba(0,0,0,0.04);
	transition: box-shadow 0.18s, border-color 0.18s;
}

.conn-row:hover {
	border-color: rgba(214, 182, 116, 0.4);
	box-shadow: 0 4px 16px rgba(214, 182, 116, 0.1);
}

/* Avatar */
.conn-row__avatar-wrap {
	position: relative;
	flex-shrink: 0;
}

.conn-row__avatar {
	width: 44px;
	height: 44px;
	border-radius: 50%;
	object-fit: cover;
	border: 2px solid #f0ece3;
	transition: border-color 0.18s;
}

.conn-row:hover .conn-row__avatar {
	border-color: rgba(214, 182, 116, 0.5);
}

.conn-row__dot {
	position: absolute;
	bottom: 1px;
	right: 1px;
	width: 11px;
	height: 11px;
	border-radius: 50%;
	border: 2px solid #fff;
}

.conn-row__dot--available   { background: linear-gradient(135deg, #28a745 0%, #218838 100%); }
.conn-row__dot--unavailable { background: #ccc; }

/* Info */
.conn-row__info {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 3px;
}

.conn-row__name {
	font-weight: 600;
	font-size: 0.92rem;
	color: #222;
	text-decoration: none;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	transition: color 0.15s;
}

.conn-row__name:hover { color: #d6b674; text-decoration: none; }

.conn-row__status {
	font-size: 0.72rem;
	font-weight: 600;
	letter-spacing: 0.4px;
}

.conn-row__status--available   { color: #28a745; }
.conn-row__status--unavailable { color: #bbb; }

/* Action buttons */
.conn-row__actions {
	display: flex;
	align-items: center;
	gap: 6px;
	flex-shrink: 0;
}

.conn-action-btn {
	width: 36px;
	height: 36px;
	border-radius: 10px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 1rem;
	text-decoration: none;
	transition: background 0.15s, color 0.15s, transform 0.13s;
	cursor: pointer;
}

.conn-action-btn--process {
	background: rgba(214, 182, 116, 0.1);
	border: 1px solid rgba(214, 182, 116, 0.3);
	color: #d6b674;
}

.conn-action-btn--process:hover {
	background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
	border-color: transparent;
	color: #fff;
	transform: scale(1.08);
}

.conn-action-btn--chat {
	background: rgba(214, 182, 116, 0.1);
	border: 1px solid rgba(214, 182, 116, 0.3);
	color: #d6b674;
}

.conn-action-btn--chat:hover {
	background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
	border-color: transparent;
	color: #fff;
	transform: scale(1.08);
}

@media (max-width: 480px) {
	.conn-row { padding: 12px 12px; }
	.conn-row__avatar { width: 38px; height: 38px; }
}

/* ── Pending Requests Section (matches Community page) ── */
.section-heading {
	border-left: 4px solid #d6b674;
	padding-left: 12px;
	color: #333;
	font-weight: 600;
}

.member-status-dot {
	position: absolute;
	top: -1px;
	right: -1px;
	width: 12px;
	height: 12px;
	border-radius: 50%;
	border: 2px solid white;
	cursor: default;
}

.member-status-dot.dot-available {
	background: linear-gradient(135deg, #28a745 0%, #218838 100%);
}

.member-status-dot.dot-unavailable {
	background-color: #ccc;
}

.member-row-name-link {
	text-decoration: none;
	color: inherit;
}

.member-row-name-link:hover .member-row-name {
	text-decoration: underline;
	color: #d6b674;
}

.btn-gold {
	background: #fff !important;
	color: #d6b674 !important;
	border: 1px solid #d6b674 !important;
	border-radius: 6px;
}

.btn-gold:hover,
.btn-gold:focus,
.btn-gold:active {
	background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%) !important;
	color: #fff !important;
	border-color: #d6b674 !important;
}

@media (max-width: 576px) {
	.btn-gold {
		font-size: 0.7rem !important;
		padding: 3px 7px !important;
	}
	.btn-gold .btn-text {
		display: none;
	}
}

.request-row {
	display: flex;
	align-items: center;
	gap: 14px;
	padding: 12px 14px;
	background: linear-gradient(90deg, rgba(214,182,116,0.08) 0%, rgba(214,182,116,0.02) 100%);
	border: 1px solid rgba(214,182,116,0.4);
	border-radius: 12px;
	margin-bottom: 10px;
}

.member-row-avatar-wrap {
	position: relative;
	flex-shrink: 0;
}

.member-row-avatar {
	width: 50px;
	height: 50px;
	border-radius: 50%;
	object-fit: cover;
	border: 2px solid #eee;
}

.member-row-info {
	flex: 1;
	min-width: 0;
}

.member-row-name {
	font-weight: 600;
	font-size: 0.9rem;
	margin-bottom: 2px;
	color: #333;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.member-row-location {
	font-size: 0.78rem;
	margin-bottom: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.member-row-actions {
	flex-shrink: 0;
	display: flex;
	align-items: center;
	gap: 6px;
	flex-wrap: wrap;
	justify-content: flex-end;
}
</style>
